import { tryGetDirectory, walkFiles } from "../../storage/fsa.ts";
import { formatBytesPlain } from "../shared/index-page.ts";
import { writeModuleIndex } from "../shared/module-index.ts";
import { ONEDRIVE_DIR } from "./paths.ts";

/**
 * Rebuilds `onedrive/index.html` by walking the mirrored tree already on
 * disk — the same idempotent, network-free, read-from-disk-not-memory
 * pattern as every other module's index render pass, adapted for OneDrive's
 * lack of a separate raw/metadata directory (see storage/fsa.ts's walkFiles).
 * Safe to re-run after every resume.
 */
export async function renderOneDriveIndex(root: FileSystemDirectoryHandle): Promise<void> {
    const dir = await tryGetDirectory(root, [ONEDRIVE_DIR]);
    if (!dir) {
        return;
    }

    const entries: {
        path: string;
        cells: Record<string, string>;
        sortValues: Record<string, number>;
        href: string;
    }[] = [];

    for await (const { relativePath, handle } of walkFiles(dir)) {
        // The generated index.html / index.json live at the top of this same
        // directory — exclude them so the page never lists itself.
        if (relativePath === "index.html" || relativePath === "index.json") {
            continue;
        }

        const file = await handle.getFile();
        const slash = relativePath.lastIndexOf("/");
        const name = slash === -1 ? relativePath : relativePath.slice(slash + 1);

        entries.push({
            path: relativePath,
            cells: {
                name,
                path: relativePath,
                size: formatBytesPlain(file.size)
            },
            sortValues: { size: file.size },
            href: relativePath
        });
    }

    entries.sort((a, b) => a.path.localeCompare(b.path));
    const rows = entries.map(({ path: _path, ...row }) => row);

    await writeModuleIndex(root, {
        moduleId: "onedrive",
        itemKind: "file",
        title: "OneDrive",
        subtitle: `${rows.length} file${rows.length === 1 ? "" : "s"} exported`,
        columns: [
            { key: "name", label: "File" },
            { key: "path", label: "Path" },
            { key: "size", label: "Size", numeric: true }
        ],
        rows,
        emptyMessage: "No files exported yet.",
        searchPlaceholder: "Filter files…"
    });
}
